<template>
  <!-- 用户登录页面 -->
  <div class="p-4 md:p-6 auth-page-scroll-fix fade-in" style="min-height: 100vh; padding-bottom: 4rem;">
    <div class="max-w-md mx-auto pt-4 md:pt-8">
      <!-- 返回按钮 -->
      <router-link to="/"
        class="inline-flex items-center gap-2 text-text-helper hover:text-primary transition-all duration-200 hover:scale-105 mb-8">
        <img style="width: 60px;" src="../../assets/images/back_icon.png">
      </router-link>

      <!-- 背景装饰 -->
      <div class="absolute inset-0 -z-10">
        <div class="absolute inset-0 bg_leaves"></div>
      </div>
      <div class="absolute inset-0 -z-10">
        <div class="absolute inset-0 bg_link"></div>
      </div>

      <!-- 登录表单卡片 -->
      <div class="mb-8 mt-16 slide-in-up">

        <!-- 机构Logo -->
        <div class="mb-20">
          <!-- <div class="w-20 mx-auto flex items-center justify-center text-6xl font-bold text-color">
            VerdeX
          </div>
          <div class="text-lg text-center">
            CARBON EXCHANGE
          </div> -->
          <img class="w-60 mx-auto" src="../../assets/airlogo.png"/>
        </div>

        <!-- 标题 -->
        <h1 class="title text-center text-primary">
          User Login
        </h1>

        <!-- 登录表单 -->
        <form @submit.prevent="handleLogin" class="space-y-6">

          <!-- 邮箱输入框 -->
          <div>
            <input id="email" v-model="form.email" type="email"
              class="input-field transform transition-all duration-200 focus:scale-[1.02] input-box"
              :class="{ error: errors.email }" placeholder="Enter your email address" required autocomplete="email"
              autofocus>
            <div v-if="errors.email" class="error-message">
              {{ errors.email }}
            </div>
          </div>

          <!-- 密码输入框 -->
          <div>
            <div class="relative">
              <input id="password" v-model="form.password" :type="showPassword ? 'text' : 'password'"
                class="input-field pr-10 transform transition-all duration-200 focus:scale-[1.02] input-box"
                :class="{ error: errors.password }" placeholder="Enter your password" required
                autocomplete="current-password">
              <button type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-text-helper hover:text-primary transition-colors p-1 rounded"
                @click="showPassword = !showPassword" :aria-label="showPassword ? 'Hide password' : 'Show password'">
                <n-icon size="18">
                  <EyeOutline v-if="!showPassword" />
                  <EyeOffOutline v-else />
                </n-icon>
              </button>
            </div>
            <div v-if="errors.password" class="error-message">
              {{ errors.password }}
            </div>
          </div>

          <!-- 记住我和忘记密码 -->
          <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <!-- <label class="checkbox">
              <input v-model="form.rememberMe" type="checkbox">
              <span class="text-sm">
                Remember me
              </span>
            </label> -->

            <!-- <router-link to="/forgot-password"
              class="text-center hover:underline text-sm font-medium transition-all duration-200 hover:scale-105">
              Forgot Password?
            </router-link> -->
          </div>

          <!-- 登录按钮 -->
          <button type="submit"
            class="btn btn-primary btn-large w-full mt-8 transform transition-all duration-200 hover:scale-105 active:scale-95 btn-full-gree"
            :disabled="loading || !isFormValid">
            <div v-if="loading" class="loading-dots">
              <div class="loading-dot"></div>
              <div class="loading-dot"></div>
              <div class="loading-dot"></div>
            </div>
            <span v-else>Login</span>
          </button>

          <!-- 注册链接 -->
          <!-- <div class="text-center mt-4">
            <p class="text-gray-600 text-sm">
              New user? 
              <router-link to="/register" class="hover:text-green-700 hover:underline font-medium" style="font-weight: 700;">
                Sign Up
              </router-link>
            </p>
          </div> -->

          <!-- 忘记密码 -->
          <div class="text-center mt-4">
            <p class="text-gray-600 text-sm" @click="goToTopUp">
              Forgot your password? Contact Us
            </p>
          </div>

          <!-- 客服联系按钮 - 自定义聊天界面 -->
          <LiveChatWidget style="display: none;" ref="liveChatWidgetRef" :auto-load="true" :show-error="false"/>

        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { NIcon, useMessage } from 'naive-ui'
import { ArrowBack, EyeOutline, EyeOffOutline } from '@vicons/ionicons5'
import { useAuthStore } from '@/stores/auth'
import { pageStateManager } from '@/utils/pageStateManager'
import { useUserStore } from '@/stores/user'
import LiveChatWidget from '@/components/common/LiveChatWidget.vue'
import { useLiveChatStore } from '@/stores/livechat'

// 路由和消息
const router = useRouter()
const message = useMessage()
const authStore = useAuthStore()
const userStore = useUserStore();
const liveChatWidgetRef = ref()
const liveChatStore = useLiveChatStore()

// 表单数据
const form = ref({
  email: '',
  password: '',
  rememberMe: false
})

// 表单状态
const loading = ref(false)
const showPassword = ref(false)
const errors = ref<Record<string, string>>({})

// 表单验证
const isFormValid = computed(() => {
  return form.value.email && form.value.password
})

/**
 * 验证表单数据
 */
const validateForm = (): boolean => {
  errors.value = {}

  // 邮箱验证
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(form.value.email)) {
    errors.value.email = 'Please enter a valid email address'
  }

  // 密码验证
  if (form.value.password.length < 6) {
    errors.value.password = 'Password must be at least 6 characters'
  }

  // 如果有错误，添加摇摆动画
  if (Object.keys(errors.value).length > 0) {
    const formElement = document.querySelector('.card')
    if (formElement) {
      formElement.classList.add('shake')
      setTimeout(() => {
        formElement.classList.remove('shake')
      }, 500)
    }
  }

  return Object.keys(errors.value).length === 0
}

/**
 * 处理登录提交
 */
const handleLogin = async () => {
  if (!validateForm()) {
    return
  }

  loading.value = true
  pageStateManager.startPageLoad('Login')

  try {
    const result = await authStore.login({
      email: form.value.email,
      password: form.value.password,
      remember: form.value.rememberMe
    })
    if (!form.value.rememberMe) {
      localStorage.removeItem('remember_user');
    }

    if (result.success) {
      if (form.value.rememberMe) {
        localStorage.setItem('remember_user', JSON.stringify(form.value))
      }

      userStore.fetchProfile().catch(e => {
        console.error('用户资料获取失败:', e)
        return null
      })

      message.success('Login successful! Welcome back.')
      pageStateManager.finishPageLoad('Login')

      // 检查用户是否完成了问卷
      if (result.hasCompletedQuestionnaire) {
        // 已完成问卷，跳转到仪表板
        console.log('登录成功，跳转到仪表板')
        router.push('/dashboard')
      } else {
        // 未完成问卷，跳转到问卷页面
        console.log('登录成功，跳转到问卷页面')
        message.info('Please complete the carbon footprint questionnaire first.')
        router.push('/questionnaire')
      }
    } else {
      message.error(result.message || 'Login failed')
      pageStateManager.finishPageLoad('Login', 'Login failed')

      // 清空密码字段
      form.value.password = ''
    }
  } catch (error) {
    message.error('Login failed. Please check your credentials and try again.')
    pageStateManager.finishPageLoad('Login', 'Login error')
    form.value.password = ''
  } finally {
    loading.value = false
  }
}

const goToTopUp = async () => {
  // 按鈕只在 isLiveChatReady=true 時才可點擊，這裡只負責開窗
  try {
    console.log('打开 LiveChat 進行充值諮詢...')

    if (liveChatWidgetRef.value && typeof liveChatWidgetRef.value.handleChatClick === 'function') {
      await liveChatWidgetRef.value.handleChatClick()
      console.log('LiveChat打开成功')
    } else {
      // 防禦性備用：直接開窗
      liveChatStore.openChatWindow()
    }
  } catch (error) {
    console.error('打开LiveChat失败:', error)
    message.error('The customer service system is temporarily unavailable, please try again later.')
  }
}

// 页面挂载时重置状态
onMounted(() => {
  const remember_user = localStorage.getItem('remember_user')
  if (remember_user) {
    form.value = JSON.parse(remember_user)
  }
  pageStateManager.finishPageLoad('Login')
})
</script>